import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDPageContainer } from '../primitives/CDPageContainer';
import { CDCostsAtClosing, type CostsAtClosingData } from '../sections/CDCostsAtClosing';
import { CDLoanTerms, type LoanTermsData } from '../sections/CDLoanTerms';
import { CDPageHeader, type CDPageHeaderData } from '../sections/CDPageHeader';
import { CDProjectedPayments, type ProjectedPaymentsData } from '../sections/CDProjectedPayments';

export interface CDPage1Data {
  header: Partial<CDPageHeaderData>;
  loanTerms: Partial<LoanTermsData>;
  projectedPayments: Partial<ProjectedPaymentsData>;
  costsAtClosing: Partial<CostsAtClosingData>;
}

export interface CDPage1Props {
  /** Page 1 data */
  data: Partial<CDPage1Data>;
  /** Total number of pages */
  totalPages?: number;
  /** Form ID for footer */
  formId?: string;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler for data updates */
  onChange?: (data: Partial<CDPage1Data>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPage1 - Complete Page 1 of Closing Disclosure
 *
 * Contains:
 * - Page header with title and transaction info
 * - Loan Terms section
 * - Projected Payments section
 * - Costs at Closing section
 * - Page footer
 */
export const CDPage1 = React.forwardRef<HTMLDivElement, CDPage1Props>(
  ({ data, totalPages = 5, formId, editable = false, onChange, className }, ref) => {
    const handleHeaderChange = (header: Partial<CDPageHeaderData>) => {
      onChange?.({ ...data, header });
    };

    const handleLoanTermsChange = (loanTerms: Partial<LoanTermsData>) => {
      onChange?.({ ...data, loanTerms });
    };

    const handleProjectedPaymentsChange = (projectedPayments: Partial<ProjectedPaymentsData>) => {
      onChange?.({ ...data, projectedPayments });
    };

    const handleCostsAtClosingChange = (costsAtClosing: Partial<CostsAtClosingData>) => {
      onChange?.({ ...data, costsAtClosing });
    };

    return (
      <CDPageContainer
        ref={ref}
        pageNumber={1}
        totalPages={totalPages}
        formId={formId}
        className={cn(className)}
      >
        {/* Page Header */}
        <CDPageHeader data={data.header} editable={editable} onChange={handleHeaderChange} />

        {/* Loan Terms */}
        <div className="mt-4">
          <CDLoanTerms
            data={data.loanTerms || {}}
            editable={editable}
            onChange={handleLoanTermsChange}
          />
        </div>

        {/* Projected Payments */}
        <div className="mt-3">
          <CDProjectedPayments
            data={data.projectedPayments || {}}
            editable={editable}
            onChange={handleProjectedPaymentsChange}
          />
        </div>

        {/* Costs at Closing */}
        <div className="mt-3">
          <CDCostsAtClosing
            data={data.costsAtClosing || {}}
            editable={editable}
            onChange={handleCostsAtClosingChange}
          />
        </div>
      </CDPageContainer>
    );
  },
);

CDPage1.displayName = 'CDPage1';
